import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { WorkItem } from "../../types/workItems";
import { CareAssessmentRow, CareRowClickData } from "./CareAssessmentRow";
import { DismissDialog } from "./DismissDialog";
import { ResidentData, SortField, SortDirection } from "../../types/dashboard";
import { STYLES } from "../../config/styles";
import { sortData } from "../../utils/dashboardUtils";
import { SortableHeader } from "../molecules/SortableHeader";
import { normalizedWord } from "@/src/utils/helper";
import { apiService } from "../../services/apiService";
import { useApiClient } from "../../contexts/ApiClientContext";
import { clearWorkItemCache } from "../../hooks/useWorkItemsAutoRefresh";
import { trackCareQueueEvent } from "../../lib/tracking";

interface CareAssessmentsTableProps {
  workItems: WorkItem[];
  onRowClick: (data: CareRowClickData) => void;
  canDismissWorkItems?: boolean;
  groupByFacility?: boolean;
  facilityNameMap?: Record<string, string>;
}

/*
// Local filter state (client-side only)
interface LocalFilters {
  status: string;
  residentName: string;
  location: string;
  assessmentType: string;
  scheduledStart: string;
  dueDate: string;
  assignedTo: string;
  priority: string;
}*/

// Column configuration type
interface ColumnConfig {
  key: string;
  label: string;
  minWidth: string;
  sortField?: SortField;
}

// Column configuration for Care Assessments.
// NETSOL-12075: Date of Birth, Location, Admission Date and Payor are no longer
// columns — they now appear in a per-row hover-over (see CareAssessmentRow).
const CARE_COLUMNS: ColumnConfig[] = [
  { key: "actions", label: "", minWidth: "min-w-[40px]" }, // Actions column (no sorting)
  { key: "status", label: "Assessment Status", minWidth: "min-w-[120px]", sortField: "status" },
  { key: "residentName", label: "Resident Name", minWidth: "min-w-[150px]", sortField: "residentName" },
  { key: "assessmentType", label: "Assessment Type", minWidth: "min-w-[140px]", sortField: "assessmentName" },
  { key: "daysOpen", label: "Days Open", minWidth: "min-w-[100px]", sortField: "createdAt" },
  { key: "dueDate", label: "Due Date", minWidth: "min-w-[110px]", sortField: "dueDate" },
];

export function CareAssessmentsTable({ workItems, onRowClick, canDismissWorkItems = false, groupByFacility = false, facilityNameMap = {} }: CareAssessmentsTableProps) {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  // Sorting state - default to Due Date ascending
  const [sortField, setSortField] = useState<SortField | null>("dueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Collapsed facility groups
  const [collapsedFacilities, setCollapsedFacilities] = useState<Set<string>>(new Set());

  // Optimistic removal: track dismissed IDs to hide rows immediately
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Dismiss dialog state
  const [dismissTarget, setDismissTarget] = useState<ResidentData | null>(null);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: ({ workItemId, reason }: { workItemId: string; reason?: string }) => {
      if (apiClient) {
        return apiClient.dismissWorkItem(workItemId, reason);
      }
      return apiService.dismissWorkItem(workItemId, reason);
    },
    onSuccess: (_, { workItemId }) => {
      setDismissedIds((prev) => new Set(prev).add(workItemId));
      clearWorkItemCache("care");
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "work-items-auto-refresh" &&
          query.queryKey[1] === "care",
      });
      toast.success("Assessment removed from queue");
      setDismissDialogOpen(false);
      setDismissTarget(null);
    },
    onError: () => {
      toast.error("Failed to dismiss assessment");
    },
  });

  const handleDismiss = (row: ResidentData) => {
    setDismissTarget(row);
    setDismissDialogOpen(true);
  };

  const handleDismissConfirm = (reason?: string) => {
    if (dismissTarget?.work_item_id) {
      dismissMutation.mutate({ workItemId: dismissTarget.work_item_id, reason });
    }
  };

  // Handle sort column click
  const handleSort = (field: SortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      // Toggle direction if same field
      newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      // New field, default to ascending
      newDirection = "asc";
      setSortField(field);
      setSortDirection(newDirection);
    }
    trackCareQueueEvent("care_queue_sort_changed", {
      column: field,
      direction: newDirection,
      table: "care",
    });
  };
  // Map WorkItem to ResidentData for display
  const mapToResidentData = (workItem: WorkItem): ResidentData => {
    return {
      residentName: workItem.resident_name,
      dateOfBirth: workItem.date_of_birth || "N/A",
      location: workItem.room || "N/A",
      admissionDate: workItem.admission_date || "N/A",
      mdsOcType: workItem.assessment_type,
      ardDate: workItem.scheduled_start_date || "N/A",
      status: normalizedWord(workItem.status_normalized),
      assignedServices: workItem.assigned_user_name || "N/A",
      dueDate: workItem.due_date || "N/A",
      wdsr02: "N/A",
      referenceWindow: "N/A",
      caaCompletion: "N/A",
      carePlanCompletionDate: "N/A",
      transmissionDate: "N/A",
      requiredTransmissionDate: "N/A",
      nextAction: "N/A",
      category: "UDA",
      work_item_id: workItem.id,
      facility_id: workItem.facility_id,
      facility_name: workItem.facility_id,
      payor: workItem.payer || "N/A",
      owner: workItem.assigned_user_name || "N/A",
      assessmentName: workItem.assessment_name || "N/A",
      statusNormalized: normalizedWord(workItem.status_normalized),
      statusBucket: workItem.status_normalized,
      createdAt: workItem.created_at || undefined,
    };
  };

  // Map work items to display data, filtering out optimistically dismissed items
  const mappedData = workItems
    .filter((item) => !dismissedIds.has(item.id))
    .map(mapToResidentData);

  // Apply sorting
  const displayData = sortData(mappedData, sortField, sortDirection);

  return (
    <>
      <div className="border-2 border-solid border-border rounded-[var(--radius)] overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-auto flex-1">
          <table className={`w-full ${STYLES.container.minWidth}`}>
            {/* Table Header */}
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-border bg-muted">
                {CARE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`${column.minWidth} p-3 text-center`}
                  >
                    {column.sortField ? (
                      <div className="flex items-center justify-center">
                        <SortableHeader
                          label={column.label}
                          field={column.sortField}
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      </div>
                    ) : (
                      <span>{column.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

          {/* Filter Row */}
          {/*<thead>
            <tr className="border-b border-border bg-card">
              {CARE_COLUMNS.map((column) => (
                <th key={`filter-${column.key}`} className="p-2">
                  <Input
                    type="text"
                    placeholder="Filter..."
                    value={localFilters[column.key as keyof LocalFilters] || ""}
                    onChange={(e) => handleLocalFilterChange(column.key as keyof LocalFilters, e.target.value)}
                    className="h-8"
                  />
                </th>
              ))}
            </tr>
          </thead>*/}

          {/* Table Body */}
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td colSpan={CARE_COLUMNS.length} className="p-8 text-center text-muted-foreground">
                  No Care Assessments found
                </td>
              </tr>
            ) : (
              (() => {
                if (!groupByFacility) {
                  return displayData.map((row, index) => (
                    <CareAssessmentRow
                      key={row.work_item_id || index}
                      row={row}
                      index={index}
                      isLastRow={index === displayData.length - 1}
                      onClick={onRowClick}
                      canDismiss={canDismissWorkItems}
                      onDismiss={handleDismiss}
                    />
                  ));
                }
                const groups: { facilityId: string; facilityName: string; rows: ResidentData[] }[] = [];
                for (const row of displayData) {
                  const fid = row.facility_id || "unknown";
                  const fname = facilityNameMap[fid] || row.facility_name || fid || "Unknown Facility";
                  const existing = groups.find((g) => g.facilityId === fid);
                  if (existing) { existing.rows.push(row); }
                  else { groups.push({ facilityId: fid, facilityName: fname, rows: [row] }); }
                }
                return groups.flatMap(({ facilityId, facilityName, rows }) => {
                  const collapsed = collapsedFacilities.has(facilityId);
                  return [
                    <tr
                      key={`facility-${facilityId}`}
                      className="bg-muted/60 border-t-2 border-border cursor-pointer select-none hover:bg-muted/80"
                      onClick={() => {
                        trackCareQueueEvent("care_queue_facility_group_toggled", {
                          action: collapsedFacilities.has(facilityId) ? "expand" : "collapse",
                          table: "care",
                        });
                        setCollapsedFacilities(prev => {
                          const next = new Set(prev);
                          if (next.has(facilityId)) next.delete(facilityId); else next.add(facilityId);
                          return next;
                        });
                      }}
                    >
                      <td colSpan={CARE_COLUMNS.length} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        <span className="mr-2">{collapsed ? "▶" : "▼"}</span>
                        {facilityName} <span className="font-normal">({rows.length})</span>
                      </td>
                    </tr>,
                    ...rows.map((row, index) => (
                      <CareAssessmentRow
                        key={row.work_item_id || `${facilityId}-${index}`}
                        row={row}
                        index={index}
                        isLastRow={!collapsed && index === rows.length - 1}
                        onClick={onRowClick}
                        canDismiss={canDismissWorkItems}
                        onDismiss={handleDismiss}
                        style={collapsed ? { display: 'none' } : undefined}
                      />
                    )),
                  ];
                });
              })()
            )}
          </tbody>
          </table>
        </div>
      </div>

      <DismissDialog
        open={dismissDialogOpen}
        onOpenChange={setDismissDialogOpen}
        onConfirm={handleDismissConfirm}
        isPending={dismissMutation.isPending}
      />
    </>
  );
}
